// SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
// SPDX-License-Identifier: Apache-2.0

import { LitElement, html } from 'lit';
import { state } from 'lit/decorators/state.js';
import type { PopoverPosition, PopoverType } from '@nvidia-elements/core/internal';
import { popoverStyles, scopedRegistry, TypeNativePopoverController, useStyles } from '@nvidia-elements/core/internal';
import { ProgressRing } from '@nvidia-elements/core/progress-ring';
import styles from './page-loader.css?inline';

/**
 * @element nve-page-loader
 * @description Page Loader is a full-screen version of the `progress-ring` component, for use when the page should remain unusable as the loader displays.
 * @documentation https://nvidia.github.io/elements/docs/elements/page-loader/
 * @since 0.19.0
 * @entrypoint \@nvidia-elements/core/page-loader
 * @slot - Loading status text displayed with the progress indicator.
 * @cssprop --gap
 * @cssprop --animation-duration - Duration of page loader open/close animations
 * @csspart progress-ring - The progress ring element
 * @aria https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/progressbar_role
 */
@scopedRegistry()
export class PageLoader extends LitElement {
  /** @private */
  readonly position: PopoverPosition = 'center';

  /** @private */
  readonly popoverType: PopoverType = 'manual';

  /** @private */
  protected typeNativePopoverController = new TypeNativePopoverController<PageLoader>(this);

  /**
   * @private
   */
  @state() anchor = globalThis.document?.body;

  static styles = useStyles([popoverStyles, styles]);

  static readonly metadata = {
    tag: 'nve-page-loader',
    version: '0.0.0'
  };

  static elementDefinitions = {
    [ProgressRing.metadata.tag]: ProgressRing
  };

  /** @private */
  declare _internals: ElementInternals;

  render() {
    return html`
      <div internal-host>
        <nve-progress-ring part="progress-ring" status="accent" size="xl"></nve-progress-ring>
        <slot></slot>
      </div>
    `;
  }
}
